import { FormSelect } from '@common/components/forms';
import { FormInput } from '@common/components/forms/controls/FormInput';
import {
  IntakePetSectionQuestionModel,
  QuestionModel,
} from '@common/models';
import { LabelPosition, QuestionType } from '@common/models/Enums';
import { observer } from 'mobx-react';
import React, { useMemo } from 'react';
import { Col, Row, Form, Image } from 'react-bootstrap';
import {
  Control,
  Controller,
  useFormContext,
} from 'react-hook-form';
import { IntakeQuestionStore } from '@common/stores/IntakeQuestionStore';
import { useLookupMap } from '@common/hooks';

interface IIntakeQuestionFactoryProps {
  question: IntakePetSectionQuestionModel;
  sectionIndex: number;
  questionIndex: number;
  store: IntakeQuestionStore;
}
interface IQuestionProps {
  question: IntakePetSectionQuestionModel;
  control: Control;
  questionIndex: number;
  sectionIndex: number;
  store: IntakeQuestionStore;
}

export type ComboQuestionTypeProps = {
  questionName: string;
  followupQuestionName: string;
  question: IntakePetSectionQuestionModel | QuestionModel;
  onQuestionChange: (val: boolean) => void;
  onFollowupChange: (val: string) => void;
  store: IntakeQuestionStore;
};

const DataEntry = observer(
  ({
    question,
    control,
    questionIndex,
    sectionIndex,
    store,
  }: IQuestionProps) => {
    return (
      <FormInput
        label=""
        controlId={`sections.${sectionIndex}.dataEntry${question.intakePetSectionQuestionId}${questionIndex}`}
        labelPosition={LabelPosition.TOP}
        fieldName={`sections.${sectionIndex}.questions.${questionIndex}.stringValue`}
        data-testid={`intake-dataentry-${question.intakePetSectionQuestionId}-${questionIndex}`}
        type="text"
        control={control}
        disabled={question.isReadOnly}
        onBlur={(e) => {
          store.setStringValue(
            question.intakePetSectionQuestionId,
            e.target.value,
          );
        }}
      />
    );
  },
);

const YesNoInput = observer(
  ({
    question,
    control,
    questionIndex,
    sectionIndex,
    overrideLabel,
    checkedVal,
    store,
  }: IQuestionProps & {
    overrideLabel: string;
    checkedVal: boolean;
  }) => {
    return (
      <Controller
        control={control}
        name={`sections.${sectionIndex}.questions.${questionIndex}.bitValue`}
        render={({ field: { onChange, value, name } }) => (
          <Form.Check
            id={`sections.${sectionIndex}.questions.${questionIndex}.bitValue_${overrideLabel.toLowerCase()}`}
            data-testid={`intake-yesno-${question.intakePetSectionQuestionId}-${checkedVal ? 'yes' : 'no'}`}
            inline
            name={name}
            label={overrideLabel}
            type="radio"
            checked={value === checkedVal}
            disabled={question.isReadOnly}
            onChange={(e) => {
              if (e.target.checked) {
                store.setBitValue(
                  question.intakePetSectionQuestionId,
                  checkedVal,
                );
                onChange(checkedVal);
              }
            }}
          />
        )}
      />
    );
  },
);

const InformationalSelect = observer(
  ({
    question,
    control,
    questionIndex,
    sectionIndex,
    store,
  }: IQuestionProps) => {
    const fieldName =
      `sections.${sectionIndex}.questions.${questionIndex}.selectedListValueId` as const;

    const options = question.intakePetSectionQuestionListValues ?? [];

    const optionLookup = useLookupMap(options, (o) =>
      o.intakePetSectionQuestionListValueId.toString(),
    );

    return (
      <FormSelect
        label=""
        fieldName={fieldName}
        data-testid={`intake-select-${question.intakePetSectionQuestionId}`}
        control={control}
        options={options}
        isRequired={false}
        getOptionLabel={(option) => option.name}
        getOptionValue={(option) =>
          option.intakePetSectionQuestionListValueId.toString()
        }
        mapValueToOption={(val) => optionLookup.get(val?.toString())}
        mapOptionToValue={(selected) => {
          const selectedId = selected?.intakePetSectionQuestionListValueId;
          store.setListValueSelected(
            question.intakePetSectionQuestionId,
            selectedId ? [selectedId] : [],
          );
          return selectedId ?? null;
        }}
        isDisabled={question.isReadOnly}
      />
    );
  },
);

const RadioList = observer(
  ({
    question,
    control,
    questionIndex,
    sectionIndex,
    store,
  }: IQuestionProps) => {
    const options = question.intakePetSectionQuestionListValues ?? [];

    return (
      <Controller
        control={control}
        name={`sections.${sectionIndex}.questions.${questionIndex}.selectedListValueId`}
        render={({ field: { value, onChange, name } }) => (
          <>
            {options.map((opt) => {
              const optId = opt.intakePetSectionQuestionListValueId;
              return (
                <Form.Check
                  key={`radio-${question.intakePetSectionQuestionId}-${optId}`}
                  type="radio"
                  name={name}
                  id={`intakeRadioGroup${question.intakePetSectionQuestionId}-${optId}`}
                  data-testid={`intake-radio-${question.intakePetSectionQuestionId}-${optId}`}
                  label={opt.name}
                  value={optId}
                  checked={value === optId}
                  disabled={question.isReadOnly}
                  onChange={(e) => {
                    if (!e.target.checked) return;
                    store.setListValueSelected(
                      question.intakePetSectionQuestionId,
                      [optId],
                    );
                    onChange(optId);
                  }}
                />
              );
            })}
          </>
        )}
      />
    );
  },
);

const CheckList = observer(
  ({
    question,
    control,
    questionIndex,
    sectionIndex,
    store,
  }: IQuestionProps) => {
    const items = question.intakePetSectionQuestionListValues ?? [];

    return (
      <>
        {items.map((lv, idx) => (
          <Controller
            key={`single-${question.intakePetSectionQuestionId}-${lv.intakePetSectionQuestionListValueId}`}
            name={`sections.${sectionIndex}.questions.${questionIndex}.intakePetSectionQuestionListValues.${idx}.isSelected`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <Form.Check
                label={lv.name}
                type="checkbox"
                id={`intakeCheckboxGroup-${question.intakePetSectionQuestionId}-${lv.intakePetSectionQuestionListValueId}`}
                data-testid={`intake-checkbox-${question.intakePetSectionQuestionId}-${lv.intakePetSectionQuestionListValueId}`}
                name={`intakeCheckboxGroup${question.intakePetSectionQuestionId}`}
                checked={!!value}
                disabled={question.isReadOnly}
                onChange={(e) => {
                  const checked = e.target.checked;
                  store.setListValueIsSelected(
                    lv.intakePetSectionQuestionListValueId,
                    checked,
                  );
                  onChange(checked);
                }}
              />
            )}
          />
        ))}
      </>
    );
  },
);

const ComboQuestionType = observer(
  ({
    questionName,
    followupQuestionName,
    question,
    onQuestionChange,
    onFollowupChange,
    store,
  }: ComboQuestionTypeProps) => {
    const { control, watch } = useFormContext();
    const bitValue = watch(questionName as any) as boolean | undefined;
    const showFollowUp = bitValue;

    const questionId =
      question instanceof IntakePetSectionQuestionModel
        ? question.intakePetSectionQuestionId
        : undefined;

    return (
      <>
        <Controller
          control={control}
          name={questionName as any}
          render={({ field: { value, onChange, name } }) => (
            <>
              <Form.Check
                inline
                name={name}
                label={question.yesOverrideText || 'Yes'}
                data-testid={`intake-combo-${questionId}-yes`}
                type="radio"
                checked={value === true}
                disabled={question.isReadOnly}
                onChange={(e) => {
                  if (!e.target.checked) return;
                  if (questionId !== undefined)
                    store.setBitValue(questionId, true);
                  onQuestionChange(true);
                  onChange(true);
                }}
              />
              <Form.Check
                inline
                name={name}
                label={question.noOverrideText || 'No'}
                data-testid={`intake-combo-${questionId}-no`}
                type="radio"
                checked={value === false}
                disabled={question.isReadOnly}
                onChange={(e) => {
                  if (!e.target.checked) return;
                  if (questionId !== undefined)
                    store.setBitValue(questionId, false);
                  onQuestionChange(false);
                  onChange(false);
                }}
              />
            </>
          )}
        />

        {showFollowUp === question.showFollowupWhenAnswerIs ? (
          <div className="mt-2">
            <Form.Label
              className={`ps-0 mb-0 ${!question.isFollowupReadOnly ? 'question' : ''}`}
              dangerouslySetInnerHTML={{ __html: question.followupQuestion }}
            />
            {!question.isFollowupReadOnly ? (
              <Controller
                control={control}
                name={followupQuestionName as any}
                render={({ field: { value, onChange } }) => (
                  <Form.Control
                    type="text"
                    value={value ?? ''}
                    name={followupQuestionName}
                    disabled={question.isReadOnly}
                    data-testid={`intake-combo-followup-${questionId}`}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (questionId !== undefined)
                        store.setStringValue(questionId, v);
                      onFollowupChange(v);
                      onChange(v);
                    }}
                  />
                )}
              />
            ) : null}
          </div>
        ) : null}
      </>
    );
  },
);

export const IntakeQuestionFactory = observer(
  ({
    question,
    questionIndex,
    sectionIndex,
    store,
  }: IIntakeQuestionFactoryProps) => {
    const { control, setValue } = useFormContext();

    const renderedElement = useMemo(() => {
      switch (question.questionTypeId) {
        case QuestionType.DataEntry:
          return (
            <DataEntry
              store={store}
              control={control}
              question={question}
              questionIndex={questionIndex}
              sectionIndex={sectionIndex}
            />
          );
        case QuestionType.YesNo:
          return (
            <Row>
              <Col sm={6} md={6}>
                <Row>
                  <Col>
                    <YesNoInput
                      store={store}
                      control={control}
                      question={question}
                      questionIndex={questionIndex}
                      sectionIndex={sectionIndex}
                      checkedVal={true}
                      overrideLabel={question.yesOverrideText || 'Yes'}
                    />
                  </Col>
                  <Col>
                    <YesNoInput
                      store={store}
                      control={control}
                      question={question}
                      questionIndex={questionIndex}
                      sectionIndex={sectionIndex}
                      checkedVal={false}
                      overrideLabel={question.noOverrideText || 'No'}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          );
        case QuestionType.List:
          if (!question.allowMultiple && question.useMultiChoiceDropDown)
            return (
              <InformationalSelect
                control={control}
                question={question}
                questionIndex={questionIndex}
                sectionIndex={sectionIndex}
                store={store}
              />
            );
          if (!question.allowMultiple && !question.useMultiChoiceDropDown)
            return (
              <RadioList
                control={control}
                question={question}
                questionIndex={questionIndex}
                sectionIndex={sectionIndex}
                store={store}
              />
            );
          if (question.allowMultiple)
            return (
              <CheckList
                control={control}
                question={question}
                questionIndex={questionIndex}
                sectionIndex={sectionIndex}
                store={store}
              />
            );
          return null;
        case QuestionType.Informational:
          return <Image src={question.photoUrl} fluid />;
        case QuestionType.Combo:
          return (
            <ComboQuestionType
              store={store}
              questionName={`sections.${sectionIndex}.questions.${questionIndex}.bitValue`}
              followupQuestionName={`sections.${sectionIndex}.questions.${questionIndex}.stringValue`}
              question={question}
              onQuestionChange={(val: boolean) =>
                setValue(
                  `sections.${sectionIndex}.questions.${questionIndex}.bitValue`,
                  val,
                )
              }
              onFollowupChange={(val: string) =>
                setValue(
                  `sections.${sectionIndex}.questions.${questionIndex}.stringValue`,
                  val,
                )
              }
            />
          );
        default:
          return null;
      }
    }, [question, control, sectionIndex, questionIndex, setValue, store]);

    if (!renderedElement) return null;

    return (
      <>
        <Row className="mb-4">
          <Col xs={12} md={12}>
            <span
              className={`${question.questionTypeId != QuestionType.Informational ? 'fw-bold' : ''}`}
            >
              {question.name}
            </span>
          </Col>
          <Col xs={12} md={12}>
            {renderedElement}
          </Col>
        </Row>
      </>
    );
  },
);
